//  ----------      START   IMPORT  BLOCK       ----------
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import BaseLayout from "../BaseLayout/BaseLayout";
import { Card, Container, Spinner, Button } from "react-bootstrap";
//  Widgets
import LoginWidget from "../../../../LoginWidget/LoginWidget";
import RegisterForm from "../RegisterWidget/RegiseterWidget";

//  Hooks
import { useFullName } from "../../../../../API/Hooks/useGeneralInfo";
//  Context
import { useLanguage } from "../../../../../API/Context/LanguageContext";
//  Config
import VocabInvitation from "../../../../../ConfigFiles/multilingual/VocabInvitation";
//  ----------      END   IMPORT  BLOCK       ----------

type InvitationParams = {
  username?: string;
};

type ActiveView = "invite" | "login" | "register";

const InvitationWidget: React.FC = () => {
  const { lan } = useLanguage();
  const { username } = useParams<InvitationParams>();

  const [activeView, setActiveView] = useState<ActiveView>("invite");
  const [targetTab, setTargetTab] = useState<string>("privacy");

  const { fullName, isLoading } = useFullName(username);

  const handleOpenLogin = (tab: string) => {
    setTargetTab(tab);
    setActiveView("login");
  };

  return (
    <BaseLayout>
      <Container className="my-5" style={{ maxWidth: "500px" }}>
        {/* Back Button when inside Login/Register views */}
        {activeView !== "invite" && (
          <div className="mb-3">
            <Button
              variant="link"
              className="p-0 text-decoration-none fw-semibold d-inline-flex align-items-center"
              onClick={() => setActiveView("invite")}
            >
              ← {lan === 1 ? "Back to Invitation" : "Volver a la invitación"}
            </Button>
          </div>
        )}

        {/* Login View embedded cleanly inside the invitation flow */}
        {activeView === "login" && (
          <div className="animate-fade-in">
            <LoginWidget targetUsername={username} targetTab={targetTab} />
          </div>
        )}

        {/* Register View */}
        {activeView === "register" && (
          <div className="animate-fade-in">
            <RegisterForm />
          </div>
        )}

        {/* Main Invitation Card View */}
        {activeView === "invite" && (
          <Card className="shadow-lg border-0 rounded-xl p-3 p-md-4 animate-fade-in">
            <Card.Body>
              {isLoading ? (
                <div
                  className="d-flex flex-column justify-content-center align-items-center"
                  style={{ minHeight: "220px" }}
                >
                  <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <h2 className="fw-bold mb-3">
                      {VocabInvitation.greeting[lan]}
                    </h2>

                    <h3 className="fw-bold fs-4 mb-1">
                      {fullName}{" "}
                      {username && (
                        <span className="username-highlight">
                          @{username}
                        </span>
                      )}
                    </h3>

                    <p className="text-muted fs-6 mb-0">
                      {VocabInvitation.has_invited_you[lan]}
                    </p>
                  </div>

                  <ul className="list-group list-group-flush text-start fs-6">
                    {/* Action 1: Add to Whitelist */}
                    <li className="list-group-item bg-transparent px-0 py-2 border-0 d-flex align-items-start">
                      <span className="me-2 text-primary fw-bold">•</span>
                      <div>
                        <Button
                          variant="link"
                          className="p-0 border-0 fw-semibold text-decoration-none align-baseline me-1"
                          onClick={() => handleOpenLogin("privacy")}
                        >
                          {VocabInvitation.action_login_link[lan]}
                        </Button>
                        <span>{VocabInvitation.action_login_suffix[lan]}</span>
                      </div>
                    </li>

                    {/* Action 2: Calendar / Search Doctor */}
                    <li className="list-group-item bg-transparent px-0 py-2 border-0 d-flex align-items-start">
                      <span className="me-2 text-primary fw-bold">•</span>
                      <div>
                        <Button
                          variant="link"
                          className="p-0 border-0 fw-semibold text-decoration-none align-baseline me-1"
                          onClick={() => handleOpenLogin("search")}
                        >
                          {VocabInvitation.action_calendar_link[lan]}
                        </Button>
                        <span>{VocabInvitation.action_calendar_suffix[lan]}</span>
                      </div>
                    </li>

                    {/* Action 3: Register */}
                    <li className="list-group-item bg-transparent px-0 py-2 border-0 d-flex align-items-start">
                      <span className="me-2 text-primary fw-bold">•</span>
                      <div>
                        <span>{VocabInvitation.action_register_prefix[lan]}</span>
                        <Button
                          variant="link"
                          className="p-0 border-0 fw-semibold text-decoration-none align-baseline mx-1"
                          onClick={() => setActiveView("register")}
                        >
                          {VocabInvitation.action_register_link[lan]}
                        </Button>
                        <span>{VocabInvitation.action_register_suffix[lan]}</span>
                      </div>
                    </li>
                  </ul>
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </BaseLayout>
  );
};

export default InvitationWidget;